import { useEffect, useRef } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { DateTime } from 'luxon';
import { getMinBetweenNextHourAndEndOfDay, getStandardTime, IBookingForm, IDateTime } from '@jooxter/utils';

const ONE_HOUR_IN_MINUTES = 60;

/**
 * README: when the range is exactly one hour long and the user has not set the end themselves,
 * moving the start keeps that one-hour duration. Anything else is left alone - a two-hour booking
 * moved to 13:45 keeps its 14:00 end, and a half-hour one stays half an hour.
 *
 * "The user has not set the end themselves" is a latch seeded from the range the form opened with,
 * not re-derived from the current values. The rule writes the followed end without `shouldDirty` so
 * it is never mistaken for a user choice - which also means a late `values` re-sync (resource
 * options arriving) reverts that end while keeping the dirty start; the effect re-applies the
 * shadowed end rather than leaving the range inverted.
 */
export const useKeepBookingDuration = (
  form: UseFormReturn<IBookingForm>,
  defaultRange: { start: IDateTime; end: IDateTime }
) => {
  const { control, setValue, trigger } = form;
  const watchedStart = useWatch({ control, name: 'start' });
  const watchedEnd = useWatch({ control, name: 'end' });
  const isEndDirty = !!form.formState.dirtyFields.end;

  // The end shadows the start one hour behind until the user sets an end of their own. `useRef`'s
  // initializer keeps this seed even though `defaultRange` is a fresh object on every late re-sync.
  const isFollowing = useRef(
    defaultRange.end.date.diff(defaultRange.start.date, 'minutes').minutes === ONE_HOUR_IN_MINUTES
  );
  // The exact end this hook last wrote, so an end coming back from the form can be told apart from
  // one the user typed (dirty + different) versus one a re-sync reverted (not dirty + different).
  const lastWrittenEnd = useRef<DateTime | undefined>(undefined);

  // The linter reads writing back into the form as "passing data to a parent". `form` is the form
  // controller, not a parent component, and the start change originates in JxtEventFormRange (shared
  // UI we do not control), so there is no event handler to move this into.
  /* eslint-disable react-you-might-not-need-an-effect/no-pass-data-to-parent */
  useEffect(() => {
    const start = watchedStart.date;
    const end = watchedEnd.date;
    const isOwnWrite = !!lastWrittenEnd.current && end.equals(lastWrittenEnd.current);

    // A dirty end we did not write is the user's own choice: stop following for good.
    if (isFollowing.current && isEndDirty && !isOwnWrite) {
      isFollowing.current = false;
    }

    if (!isFollowing.current || !start.isValid) {
      return;
    }

    const nextEnd = getMinBetweenNextHourAndEndOfDay(start);

    if (nextEnd.equals(end)) {
      return;
    }

    lastWrittenEnd.current = nextEnd;
    setValue('end', { date: nextEnd, time: getStandardTime(nextEnd) }, { shouldTouch: false });
    // The "end must be after start" rule reports on `start.date` (and blanks out `end.date`,
    // `start.time`, `end.time` to flag them), so moving `end` has to re-validate `start` as well -
    // setValue's own `shouldValidate` would only clear the error sitting on `end`.
    trigger(['start', 'end']);
  }, [watchedStart, watchedEnd, isEndDirty, setValue, trigger]);
  /* eslint-enable react-you-might-not-need-an-effect/no-pass-data-to-parent */
};
